<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-background">
        <img 
          src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Modern office with team collaboration"
          class="hero-image"
        />
        <div class="hero-overlay"></div>
      </div>
      <div class="hero-content">
        <div class="hero-text">
          <h1 class="hero-title">About Franchise Brigade</h1>
          <p class="hero-subtitle">
            Connecting entrepreneurs with franchise opportunities across India since 2022
          </p>
          <div class="stats-container">
            <div class="stat-card">
              <p class="stat-number">{{ statsLoading ? '...' : stats.brands ? `${stats.brands}+` : '150+' }}</p>
              <p class="stat-label">Franchise Brands</p>
            </div>
            <div class="stat-card">
              <p class="stat-number">{{ statsLoading ? '...' : stats.investors ? `${stats.investors}+` : '2500+' }}</p>
              <p class="stat-label">Investors Connected</p>
            </div>
            <div class="stat-card">
              <p class="stat-number">{{ statsLoading ? '...' : stats.cities ? `${stats.cities}+` : '45+' }}</p>
              <p class="stat-label">Cities Covered</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Our Story Section -->
    <section class="story-section">
      <div class="container">
        <div class="story-grid">
          <div class="story-content">
            <h2 class="story-title">{{ aboutData?.title || 'Our Story' }}</h2>
            <div v-if="aboutLoading" class="loading-text">Loading...</div>
            <div v-else-if="aboutStatus === 'failed'" class="error-state">
              <h3>Our story is being written</h3>
              <p>We're crafting an amazing story about our journey! 📝</p>
              <button @click="fetchAbout" class="retry-btn">Try Again</button>
            </div>
            <div v-else class="story-text">{{ aboutData?.content }}</div>
          </div>
          <div class="story-image-container">
            <div class="image-decoration"></div>
            <img
              :src="aboutData?.image || 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'"
              :alt="aboutData?.title || 'Team meeting discussing franchise opportunities'"
              class="story-image"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Our Values Section -->
    <section class="values-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Our Values</h2>
          <p class="section-subtitle">
            The principles that guide us in connecting entrepreneurs with their ideal franchise opportunities.
          </p>
        </div>

        <div class="values-grid">
          <div class="value-card">
            <div class="value-icon trending">
              <span class="icon-text">📈</span>
            </div>
            <h3 class="value-title">Transparent Information</h3>
            <p class="value-description">
              We believe in providing comprehensive, honest information about every franchise opportunity, including investment requirements, expected returns, and potential challenges.
            </p>
          </div>

          <div class="value-card">
            <div class="value-icon users">
              <span class="icon-text">👥</span>
            </div>
            <h3 class="value-title">Entrepreneur-First Approach</h3>
            <p class="value-description">
              Our recommendations are always based on what's best for the entrepreneur, not what generates the highest commission. We prioritize long-term success over short-term gains.
            </p>
          </div>

          <div class="value-card">
            <div class="value-icon award">
              <span class="icon-text">🏆</span>
            </div>
            <h3 class="value-title">Quality Over Quantity</h3>
            <p class="value-description">
              We carefully vet every franchise on our platform to ensure they have proven business models, proper support systems, and ethical business practices.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Team Section -->
    <section class="team-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Meet Our Team</h2>
          <p class="section-subtitle">
            Experienced professionals dedicated to helping you find the perfect franchise match.
          </p>
        </div>

        <div v-if="teamLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading team members...</p>
        </div>
        
        <div v-else-if="teamStatus === 'failed'" class="error-state">
          <h3>Our team is assembling</h3>
          <p>Our amazing team members are getting ready to meet you! 👥</p>
          <button @click="fetchTeam" class="retry-btn">Try Again</button>
        </div>
        
        <div v-else class="team-grid">
          <div 
            v-for="(member, index) in teamMembers" 
            :key="member.id || index"
            class="team-card"
            @click="goToTeamMember(member.id)"
          >
            <div class="team-image-container">
              <router-link :to="`/team/${member.id}`">
              <img 
                :src="member.image" 
                :alt="member.name" 
                class="team-image"
              />
              </router-link>
            </div>
            <div class="team-info">
              <h3 class="team-name">{{ member.name }}</h3>
              <p class="team-position">{{ member.position }}</p>
              <p class="team-bio">{{ member.bio }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Ready to Start Your Franchise Journey?</h2>
          <p class="cta-subtitle">
            Whether you're looking to invest in a franchise or expand your brand through franchising, we're here to help.
          </p>
          <div class="cta-buttons">
            <router-link to="/franchises" class="cta-btn outline">
              Browse Franchises
            </router-link>
            <router-link to="/contactus" class="cta-btn primary">
              Contact Us
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

// Reactive data
const stats = ref({
  brands: 0,
  investors: 0,
  cities: 0
})
const statsLoading = ref(true)
const aboutData = ref({
  title: '',
  content: '',
  image: ''
})
const aboutLoading = ref(true)
const aboutStatus = ref('idle')
const teamMembers = ref([])
const teamLoading = ref(true)
const teamStatus = ref('idle')

// Methods
const fetchStats = async () => {
  statsLoading.value = true
  
  try {
    const response = await fetch('https://www.franchisebrigade.com/admin/api/stats')
    if (!response.ok) {
      throw new Error('Failed to fetch stats')
    }
    const data = await response.json()
    stats.value = {
      brands: data.brands || 0,
      investors: data.investors || 0,
      cities: data.cities || 0
    }
  } catch (error) {
    // Fallback to default stats
    stats.value = {
      brands: 150,
      investors: 2500,
      cities: 45
    }
  } finally {
    statsLoading.value = false
  }
}

const fetchAbout = async () => {
  aboutLoading.value = true
  aboutStatus.value = 'loading'
  
  try {
    const response = await fetch('https://www.franchisebrigade.com/admin/api/about')
    if (!response.ok) {
      throw new Error('Failed to fetch about data')
    }
    const data = await response.json()
    
    aboutData.value = {
      title: data.title || 'Our Story',
      content: data.content ? data.content.replace(/\\r\\n/g, '\n\n') : 'Franchise Brigade was founded with a simple mission: to democratize access to franchise opportunities across India and help entrepreneurs find the perfect business match.\n\nWhat started as a small consulting firm has grown into India\'s leading franchise discovery platform, connecting thousands of investors with hundreds of franchise brands across the country.\n\nWe believe that franchising is one of the most accessible paths to entrepreneurship, combining the independence of business ownership with the support of established systems and brands. Our platform makes finding the right franchise opportunity easier than ever before.',
      image: data.image || 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    }
    aboutStatus.value = 'success'
  } catch (error) {
    aboutStatus.value = 'failed'

    // Fallback to default content
    aboutData.value = {
      title: 'Our Story',
      content: 'Franchise Brigade was founded with a simple mission: to democratize access to franchise opportunities across India and help entrepreneurs find the perfect business match.\n\nWhat started as a small consulting firm has grown into India\'s leading franchise discovery platform, connecting thousands of investors with hundreds of franchise brands across the country.\n\nWe believe that franchising is one of the most accessible paths to entrepreneurship, combining the independence of business ownership with the support of established systems and brands. Our platform makes finding the right franchise opportunity easier than ever before.',
      image: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    }
  } finally {
    aboutLoading.value = false
  }
}

const fetchTeam = async () => {
  teamLoading.value = true
  teamStatus.value = 'loading'
  
  try {
    const response = await fetch('https://www.franchisebrigade.com/admin/api/team')
    if (!response.ok) {
      throw new Error('Failed to fetch team data')
    }
    const data = await response.json()
    
    // Transform the API response to match our component structure
    teamMembers.value = data.map(member => ({
      id: member.id,
      name: member.name,
      position: member.position,
      bio: member.bio || 'Experienced professional dedicated to helping entrepreneurs find the perfect franchise match.',
      image: member.image || 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400'
    }))
    teamStatus.value = 'success'
  } catch (error) {
    teamStatus.value = 'failed'

    // Fallback to default team members
    teamMembers.value = [
      {
        id: 1,
        name: 'Sarah Johnson',
        position: 'CEO & Founder',
        bio: 'Experienced entrepreneur with 15+ years in franchise consulting.',
        image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400'
      },
      {
        id: 2,
        name: 'Michael Chen',
        position: 'Head of Operations',
        bio: 'Operations expert specializing in franchise development and scaling.',
        image: 'https://images.pexels.com/photos/3184296/pexels-photo-3184296.jpeg?auto=compress&cs=tinysrgb&w=400'
      },
      {
        id: 3,
        name: 'Emily Rodriguez',
        position: 'Franchise Consultant',
        bio: 'Dedicated to matching entrepreneurs with their perfect franchise opportunity.',
        image: 'https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=400'
      },
      {
        id: 4,
        name: 'David Kim',
        position: 'Business Development',
        bio: 'Focused on expanding our network of quality franchise partners.',
        image: 'https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=400'
      }
    ]
  } finally {
    teamLoading.value = false
  }
}

const goToTeamMember = (id) => {
  router.push(`/team/${id}`)
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchStats(),
    fetchAbout(),
    fetchTeam()
  ])
})
</script>


<style scoped>
.about-page {
  font-family: 'Inter', Arial, sans-serif;
  color: #333;
  background: #f8f9fa;
}

/* Hero Section */
.hero-section {
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(30, 58, 138, 0.75);
  mix-blend-mode: multiply;
}

.hero-content {
  position: relative;
  z-index: 10;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  line-height: 1.1;
}

.hero-subtitle {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
}

.stats-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  min-width: 120px;
}

.stat-number {
  font-size: 2rem;
  font-weight: 900;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
}

/* Story Section */
.story-section {
  padding: 5rem 0;
  background: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.story-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.story-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #1f2937;
}

.story-text {
  font-size: 1.50rem;
  line-height: 1.7;
  color: #4b5563;
  white-space: pre-line;
}

.loading-text {
  color: #6b7280;
  font-style: italic;
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #dc2626;
}

.retry-btn {
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #1d4ed8;
}

.story-image-container {
  position: relative;
}

.image-decoration {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  border-radius: 0.5rem;
  transform: rotate(3deg) scale(1.05);
  opacity: 0.2;
}

.story-image {
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
}

/* Values Section */
.values-section {
  padding: 5rem 0;
  background: #f9fafb;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #1f2937;
}

.section-subtitle {
  font-size: 1.70rem;
  color: #6b7280;
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.value-card {
  background: white;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.value-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.value-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.value-icon.trending {
  background: #dbeafe;
  color: #2563eb;
}

.value-icon.users {
  background: #ccfbf1;
  color: #0d9488;
}

.value-icon.award {
  background: #fed7aa;
  color: #ea580c;
}

.value-title {
  font-size: 1.50rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1f2937;
}

.value-description {
  color: #6b7280;
  line-height: 1.6;
  font-size: 1.70rem;
}

.icon-text {
  font-size: 2rem;
  line-height: 1;
}

/* Team Section */
.team-section {
  padding: 5rem 0;
  background: white;
}

.loading-container {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.loading-spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid #e5e7eb;
  border-top: 3px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

.team-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  max-width: 250px;
  margin: 0 auto;
}

.team-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.team-image-container {
  height: 12rem;
  overflow: hidden;
}

.team-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.team-info {
  padding: 1rem;
}

.team-name {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #1f2937;
}

.team-position {
  color: var(--primary-blue);
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
}

.team-bio {
  color: #6b7280;
  line-height: 1.5;
  font-size: 0.8rem;
}

/* CTA Section */
.cta-section {
  padding: 4rem 0;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
}

.cta-content {
  text-align: center;
  max-width: 48rem;
  margin: 0 auto;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
}

.cta-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.cta-btn {
  padding: 0.875rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  display: inline-block;
}

.cta-btn.outline {
  border: 2px solid white;
  color: white;
  background: transparent;
}

.cta-btn.outline:hover {
  background: white;
  color: var(--primary-blue);
}

.cta-btn.primary {
  background: white;
  color: var(--primary-blue);
}

.cta-btn.primary:hover {
  background: #f3f4f6;
  color: var(--primary-blue);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.25rem;
  }
  
  .story-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .story-title,
  .section-title,
  .cta-title {
    font-size: 2rem;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
  }
  
  .stat-card {
    min-width: 200px;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
  }
  
  .team-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1.125rem;
  }
  
  .story-title,
  .section-title,
  .cta-title {
    font-size: 1.75rem;
  }
  
  .section-subtitle,
  .cta-subtitle {
    font-size: 1.125rem;
  }
}
</style>








<!-- <style scoped>
.about-page {
  font-family: 'Inter', Arial, sans-serif;
  color: #333;
  background: #f8f9fa;
}

/* Hero Section */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(30, 58, 138, 0.75);
  mix-blend-mode: multiply;
}

.hero-content {
  position: relative;
  z-index: 10;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  line-height: 1.1;
}

.hero-subtitle {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
}

.stats-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  min-width: 120px;
}

.stat-number {
  font-size: 2rem;
  font-weight: 900;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
}

/* Story Section */
.story-section {
  padding: 5rem 0;
  background: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.story-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.story-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #1f2937;
}

.story-text {
  font-size: 1.125rem;
  line-height: 1.7;
  color: #4b5563;
  white-space: pre-line;
}

.loading-text {
  color: #6b7280;
  font-style: italic;
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #dc2626;
}

.retry-btn {
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #1d4ed8;
}

.story-image-container {
  position: relative;
}

.image-decoration {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  border-radius: 0.5rem;
  transform: rotate(3deg) scale(1.05);
  opacity: 0.2;
}

.story-image {
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
}

/* Values Section */
.values-section {
  padding: 5rem 0;
  background: #f9fafb;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #1f2937;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #6b7280;
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.value-card {
  background: white;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.value-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.value-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.value-icon.trending {
  background: #dbeafe;
  color: #2563eb;
}

.value-icon.users {
  background: #ccfbf1;
  color: #0d9488;
}

.value-icon.award {
  background: #fed7aa;
  color: #ea580c;
}

.value-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1f2937;
}

.value-description {
  color: #6b7280;
  line-height: 1.6;
}

/* Team Section */
.team-section {
  padding: 5rem 0;
  background: white;
}

.loading-container {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.loading-spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid #e5e7eb;
  border-top: 3px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2rem;
  max-width: 1000px;
  margin: 0 auto;
}

.team-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  max-width: 250px;
  margin: 0 auto;
}

.team-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.team-image-container {
  height: 12rem;
  overflow: hidden;
}

.team-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.team-info {
  padding: 1rem;
}

.team-name {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #1f2937;
}

.team-position {
  color: var(--primary-blue);
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
}

.team-bio {
  color: #6b7280;
  line-height: 1.5;
  font-size: 0.8rem;
}

/* CTA Section */
.cta-section {
  padding: 4rem 0;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
}

.cta-content {
  text-align: center;
  max-width: 48rem;
  margin: 0 auto;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
}

.cta-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.cta-btn {
  padding: 0.875rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  display: inline-block;
}

.cta-btn.outline {
  border: 2px solid white;
  color: white;
  background: transparent;
}

.cta-btn.outline:hover {
  background: white;
  color: var(--primary-blue);
}

.cta-btn.primary {
  background: white;
  color: var(--primary-blue);
}

.cta-btn.primary:hover {
  background: #f3f4f6;
  color: var(--primary-blue);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.25rem;
  }
  
  .story-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .story-title,
  .section-title,
  .cta-title {
    font-size: 2rem;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
  }
  
  .stat-card {
    min-width: 200px;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
  }
  
  .team-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1.125rem;
  }
  
  .story-title,
  .section-title,
  .cta-title {
    font-size: 1.75rem;
  }
  
  .section-subtitle,
  .cta-subtitle {
    font-size: 1.125rem;
  }
}
</style> -->